// @ts-check
import { parse, differenceInMinutes } from 'date-fns';
import { buildVisitScheduleItem } from './buildVisitScheduleItem';
import { buildPadPrescriptionEvent } from './buildPadPrescriptionEvent';

/** @typedef {number} MaxTimeDifferenceMinutes  */

/**
 * Agrupa eventos com base em uma janela de tempo máxima.
 * * @param {import('./buildPadPrescriptionEvent').DtEvent[]} dtEvents - Lista de eventos (DtEvent) a serem agrupados.
 * @param {MaxTimeDifferenceMinutes} [maxTimeDifferenceMinutes=60] - Diferença máxima em minutos para agrupamento.
 * @returns {Map<string, any>} Um Map contendo as visitas agrupadas.
 */
export function applyGroupEventsByTimeGapRules(dtEvents, maxTimeDifferenceMinutes = 60) {
  // Validação rápida da entrada
  if (!Array.isArray(dtEvents) || dtEvents.length === 0) {
    return new Map();
  }

  // 1. Ordena os eventos cronologicamente para que a busca por proximidade funcione
  const dtEventSortedList = [...dtEvents].sort((a, b) => toDate(a).getTime() - toDate(b).getTime());

  const scheduleMap = new Map();

  // Array auxiliar para sabermos quais chaves/visitas já criamos e comparar os tempos
  const createdVisitsTimeline = [];

  // 2. Percorre os eventos ordenados
  // eslint-disable-next-line no-unused-vars
  for (const currentDtEvent of dtEventSortedList) {
    const { date, time, padPrescriptionId /* , padPrescription */ } = currentDtEvent;
    const currentEventDate = toDate(currentDtEvent);

    let matchedVisitKey = null;

    // Verifica se este evento se encaixa em alguma visita já criada na timeline
    // Como está ordenado, geralmente fará sentido comparar com os últimos criados
    for (let i = createdVisitsTimeline.length - 1; i >= 0; i--) {
      const existingVisit = createdVisitsTimeline[i];
      const diff = Math.abs(differenceInMinutes(currentEventDate, existingVisit.dateTime));

      if (diff <= maxTimeDifferenceMinutes) {
        matchedVisitKey = existingVisit.uniqueVisitKey;
        break; // Encontrou a visita correspondente dentro da janela de tempo
      }
    }

    if (matchedVisitKey) {
      // Se encontrou uma visita próxima, apenas adiciona o ID do medicamento a ela
      const visit = scheduleMap.get(matchedVisitKey);
      visit.padPrescriptionIds.push(padPrescriptionId);

      const nEvent = buildPadPrescriptionEvent(currentDtEvent);
      visit.padPrescriptionEvents.push(nEvent);
    } else {
      // Se não encontrou nenhuma visita no limite de tempo, cria uma NOVA visita
      const uniqueVisitKey = `${date.replace(/\//g, '-')}-${time.replace(/:/g, '')}`;
      const newItem = buildVisitScheduleItem(currentDtEvent);

      scheduleMap.set(uniqueVisitKey, {
        ...newItem,
        uniqueVisitKey
      });

      // Salva na timeline para comparar com os próximos eventos do loop
      createdVisitsTimeline.push({
        uniqueVisitKey,
        dateTime: currentEventDate
      });
    }
  }

  return scheduleMap;
}

/**
 * Converte um objeto com data e hora string para um objeto Date.
 * * @param {Object} param0 - O objeto contendo a data e a hora.
 * @param {string} param0.date - A data no formato "DD/MM/YYYY" (ex: "22/06/2026").
 * @param {string} param0.time - A hora no formato "HH:mm" ou "HH:mm:ss" (ex: "14:30").
 * @throws {Error} Se a string gerada não for uma data/hora válida.
 * @returns {Date} Um objeto Date válido.
 */
const toDate = ({ date, time }) => {
  const [day, month, year] = date.trim().split('/');
  const isoString = `${year}-${month}-${day}T${time.trim()}:00`;
  const parsed = parse(isoString);

  if (isNaN(parsed.getTime())) {
    throw new Error(`Data/hora inválida: ${date} ${time}`);
  }
  return parsed;
};
